import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'
import { type MouseEvent as ReactMouseEvent, useState } from 'react'

import { cn } from '../../lib/utils'

export interface CopyIdProps {
  /** The ID to copy. */
  id: string
  /** Optional override for the container className. */
  className?: string
}

/**
 * Inline ID + copy button. Low-emphasis mono style suited for placement
 * underneath a title (card subtitle, table cell). Click copies the ID and
 * stops propagation so a surrounding row/card click handler doesn't fire.
 */
export function CopyId({ id, className }: CopyIdProps) {
  const [copied, setCopied] = useState(false)
  const onClick = async (e: ReactMouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can fail in non-secure contexts; silently ignore.
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={copied ? 'Copied!' : 'Copy ID'}
      className={cn(
        'text-text-mute hover:text-accent inline-flex w-fit items-center gap-1.5 font-mono text-[10px] transition-colors duration-150',
        className,
      )}
    >
      {copied ? (
        <CheckIcon className="text-success h-2.5 w-2.5" />
      ) : (
        <ClipboardDocumentIcon className="h-2.5 w-2.5 opacity-60" />
      )}
      <span className="truncate">{id}</span>
    </button>
  )
}

export default CopyId
